
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { AssessmentRecord, Answer, OrganizationDetails, Industry, OrganizationSize } from "@/types/assessment";
import { Database } from "@/integrations/supabase/types";

type AssessmentSessionType = Database['public']['Tables']['assessment_sessions']['Row'];

export const useAssessmentHistory = (userEmail: string | undefined) => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ["assessments", userEmail],
    queryFn: async () => {
      if (!userEmail) {
        console.log("No user email provided, skipping fetch");
        return [];
      }
      
      console.log("Fetching assessments for email:", userEmail);
      
      try {
        // Fetch assessments using the email field instead of user_id
        const { data, error } = await supabase
          .from("assessments")
          .select("*")
          .eq("email", userEmail)
          .order("created_at", { ascending: false });

        if (error) {
          console.error("Error fetching assessments:", error);
          toast({
            title: "Error",
            description: "Failed to load assessment history",
            variant: "destructive",
          });
          throw new Error(`Failed to load assessment history: ${error.message}`);
        }

        // Create default organization details to handle undefined data
        const defaultOrgDetails: OrganizationDetails = {
          name: "Unknown Organization",
          industry: "Other" as Industry,
          size: "1-10 staff" as OrganizationSize,
          assessmentDate: new Date().toISOString()
        };

        return data.map((assessment): AssessmentRecord => {
          // Handle potential undefined values safely
          const answers = Array.isArray(assessment.answers) ? assessment.answers as Answer[] : [];
          const orgDetails = assessment.organization_details || defaultOrgDetails;
          
          // Properly handle dimension_scores type conversion
          let dimensionScores: Record<string, number> = {};
          if (assessment.dimension_scores && typeof assessment.dimension_scores === 'object' && !Array.isArray(assessment.dimension_scores)) {
            // Convert the dimension_scores from Json to Record<string, number>
            Object.entries(assessment.dimension_scores).forEach(([key, value]) => {
              if (typeof value === 'number') {
                dimensionScores[key] = value;
              }
            });
          }
          
          return {
            id: assessment.id,
            organization_details: orgDetails as OrganizationDetails,
            answers: answers,
            maturity_level: assessment.maturity_level || "Beginner",
            overall_score: assessment.overall_score || 0,
            dimension_scores: dimensionScores,
            description: assessment.description || "",
            created_at: assessment.created_at,
            created_by: assessment.email || ""
          };
        });
      } catch (err) {
        console.error("Error in assessment history hook:", err);
        toast({
          title: "Error",
          description: "Failed to load assessment data",
          variant: "destructive",
        });
        throw err;
      }
    },
    enabled: !!userEmail,
    retry: 1,
    staleTime: 0, // Data is considered stale immediately
    gcTime: 0, // Previously called cacheTime in React Query v4
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });
};
